import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { usePin } from 'context/PinContext';
import { LinearGradient } from 'expo-linear-gradient';
import { RootStackParamList } from 'navigation/AppNavigator.web';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCreateProfileMutation,
  useDeleteProfileMutation,
  useGetProfileByIdQuery,
  useUpdateProfileMutation,
} from 'services/backend';
import { mapFrontendProfileToBackend } from 'services/backend/profileMapper';
import { RootState } from 'store/index';
import { clearEditingProfile, startEditingProfile } from 'store/slices/profileSlice';
import { EDITABLE_AGE_RATINGS } from 'utils/ageRating';
import { calcSizeForDevice, getResponsiveSize } from 'utils/index';
import { Platform } from 'utils/platform';

import ProfileEditNameScreen from './EditNameScreen';
import ProfileEditPinScreen from './EditPinScreen';
import ProfileRatingSelectionScreen from './RatingSelectionScreen';

import { Icons } from 'components/Icons';
import { ProfileNameField } from 'components/Profile/Form/NameField';
import { ProfilePinEditButton } from 'components/Profile/Form/PinEditButton';
import { ProfileSectionItem } from 'components/Profile/Form/ProfileSectionItem';
import { ProfileRatingPreview } from 'components/Profile/Form/RatingPreview';
import { WebDiv } from 'components/WebDiv';
import IsLoading from 'components/isLoading';

type ProfileFormNavigation = NativeStackNavigationProp<
  RootStackParamList,
  'ProfileEdit' | 'ProfileCreate'
>;

type ProfileEditRoute = RouteProp<RootStackParamList, 'ProfileEdit'>;
type ProfileCreateRoute = RouteProp<RootStackParamList, 'ProfileCreate'>;
type Route = ProfileEditRoute | ProfileCreateRoute;

type FormView = 'form' | 'editName' | 'editPin' | 'rating';

const ProfileFormScreen = () => {
  const navigation = useNavigation<ProfileFormNavigation>();
  const route = useRoute<Route>();
  const dispatch = useDispatch();
  const { requirePin, hasPin } = usePin();

  const [view, setView] = useState<FormView>('form');
  const [nameError, setNameError] = useState<boolean>(false);

  const isCreateMode = route.name === 'ProfileCreate';
  const profileId = route.name === 'ProfileEdit' ? route.params.profileId : undefined;

  const {
    data: profileById,
    isLoading: isLoadingProfile,
    isError: isQueryError,
  } = useGetProfileByIdQuery(profileId as string, { skip: !profileId });

  const editingProfile = useSelector((state: RootState) => state.profile.editingProfile);

  const [createProfile, { isLoading: isCreatingProfile }] = useCreateProfileMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const [deleteProfile, { isLoading: isDeletingProfile }] = useDeleteProfileMutation();

  const canEditRating =
    !profileById?.isOwner &&
    !profileById?.isKid &&
    profileById?.ageRating &&
    EDITABLE_AGE_RATINGS.includes(profileById?.ageRating);

  const canDeleteProfile = !isCreateMode && !profileById?.isOwner && !profileById?.isKid;
  const isSaving = isCreatingProfile || isUpdatingProfile || isDeletingProfile;
  const isLoading = (!isCreateMode && isLoadingProfile) || isSaving;

  useEffect(() => {
    if (isCreateMode) {
      dispatch(startEditingProfile(null));
      return;
    }

    if (profileById) dispatch(startEditingProfile(profileById));
  }, [isCreateMode, profileById, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearEditingProfile());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isCreateMode && (isQueryError || (!isLoadingProfile && !profileById))) {
      navigation.replace('ProfileSelection');
    }
  }, [isCreateMode, isQueryError, isLoadingProfile, profileById, navigation]);

  const handleEditNamePress = () => (Platform.isTV ? setView('editName') : undefined);

  const handleEditPinPress = () => setView('editPin');

  const handleEditRatingPress = () => setView('rating');

  const handleSave = async () => {
    try {
      if (!editingProfile) return;

      const trimmedName = editingProfile.name?.trim() ?? '';
      if (trimmedName.length === 0) {
        setNameError(true);
        setTimeout(() => setNameError(false), 1000);
        return;
      }

      const requestData = mapFrontendProfileToBackend(editingProfile);
      const { name, rating } = requestData;

      if (isCreateMode) {
        await createProfile({ name, ageRating: rating }).unwrap();
      } else {
        await updateProfile({
          id: editingProfile.id,
          data: {
            name: editingProfile.name,
            rating: editingProfile.ageRating,
          },
        }).unwrap();
      }

      dispatch(clearEditingProfile());
      navigation.replace('ProfileSelection');
    } catch (err) {
      console.error('Error saving profile', err);
    }
  };

  const handleCancel = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.replace('ProfileSelection');
    }
  };

  const handleDeleteProfile = async () => {
    if (!profileById?.id || !canDeleteProfile) return;

    try {
      if (hasPin) {
        const isAuthorized = await requirePin();
        if (!isAuthorized) return;
      }

      await deleteProfile(profileById.id).unwrap();

      dispatch(clearEditingProfile());
      navigation.navigate('ProfileSelection');
    } catch (error) {
      console.error('Error deleting profile', error);
    }
  };

  if (view === 'editName') {
    return <ProfileEditNameScreen onClose={() => setView('form')} />;
  }

  if (view === 'editPin') {
    return <ProfileEditPinScreen onClose={() => setView('form')} />;
  }

  if (view === 'rating') {
    return <ProfileRatingSelectionScreen onClose={() => setView('form')} />;
  }

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <IsLoading />
      </View>
    );
  }

  if (!editingProfile) return null;

  return (
    <View style={[styles.content, Platform.isTV ? styles.tvContent : styles.webContent]}>
      <WebDiv className="navigation-container navigation-priority">
        <Text style={[styles.title, Platform.isTV ? styles.tvTitle : styles.webTitle]}>Olá</Text>
        <ProfileNameField
          value={editingProfile?.name || ''}
          onChange={(value) => {
            dispatch(
              startEditingProfile({
                ...editingProfile,
                name: value,
              })
            );
          }}
          onPress={handleEditNamePress}
          hasError={nameError}
        />
        <View style={styles.divider} />

        {profileById?.isOwner && (
          <>
            <ProfileSectionItem label="PIN da conta">
              <ProfilePinEditButton onPress={handleEditPinPress} />
            </ProfileSectionItem>
            <View style={styles.divider} />
          </>
        )}

        {((canEditRating && editingProfile.ageRating) || isCreateMode) && (
          <ProfileSectionItem label="Configurações de classificação etária:">
            <ProfileRatingPreview
              ageRating={editingProfile?.ageRating ?? ''}
              onPress={handleEditRatingPress}
            />
          </ProfileSectionItem>
        )}
        <View style={styles.rowField}>
          <WebDiv className="rounded-6" style={{ display: 'flex', flex: 1 }}>
            <Pressable
              accessibilityRole="button"
              onPress={handleSave}
              style={Platform.isTV ? styles.tvSaveBtnWrapper : styles.webSaveBtnWrapper}>
              <LinearGradient
                colors={['#00AD5C', '#6DFFBC']}
                start={{ x: 0, y: 1 }}
                end={{ x: 0, y: 0 }}
                style={[styles.saveBtn, Platform.isTV ? styles.tvSaveBtn : styles.webSaveBtn]}>
                <Text style={styles.saveBtnText}>Salvar</Text>
              </LinearGradient>
            </Pressable>
          </WebDiv>

          <Pressable
            accessibilityRole="button"
            style={[styles.cancelBtn, Platform.isTV ? styles.tvCancelBtn : styles.webCancelBtn]}
            onPress={handleCancel}>
            <Text style={styles.cancelBtnText}>Cancelar</Text>
          </Pressable>
        </View>

        {canDeleteProfile && (
          <Pressable
            accessibilityRole="button"
            style={styles.deleteBtn}
            onPress={handleDeleteProfile}>
            <Icons.Trash
              width={getResponsiveSize({ base: 16, md: 24 })}
              height={getResponsiveSize({ base: 16, md: 24 })}
            />
            <Text style={styles.deleteBtnText}>Excluir Perfil</Text>
          </Pressable>
        )}
      </WebDiv>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    justifyContent: 'center',
    alignSelf: 'center',
  },
  tvContent: {
    width: calcSizeForDevice(538),
    marginTop: calcSizeForDevice(71),
  },
  webContent: {
    width: '90%',
    maxWidth: 540,
  },
  title: {
    fontWeight: '600',
    color: '#FFF',
  },
  tvTitle: {
    fontSize: calcSizeForDevice(44),
    marginBottom: calcSizeForDevice(24),
  },
  webTitle: {
    fontSize: Number(getResponsiveSize({ base: 32, md: 44 })),
    marginBottom: Number(getResponsiveSize({ base: 16, md: 24 })),
    marginTop: Number(getResponsiveSize({ base: 16, md: 24 })),
  },
  rowField: {
    width: '100%',
    flexDirection: getResponsiveSize({ base: 'column', md: 'row' }),
    justifyContent: 'space-between',
    marginTop: calcSizeForDevice(48),
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: 'rgba(209, 215, 255, 0.2)',
    marginBottom: calcSizeForDevice(24),
  },
  tvSaveBtnWrapper: {
    flex: 1,
    marginRight: calcSizeForDevice(24),
  },
  webSaveBtnWrapper: {
    flex: 1,
    marginRight: Number(getResponsiveSize({ base: 0, md: 24 })),
  },
  saveBtn: {
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tvSaveBtn: {
    height: calcSizeForDevice(54),
  },
  webSaveBtn: {
    width: '100%',
    maxWidth: 366,
    height: 54,
  },
  saveBtnText: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '600',
    color: '#070D31',
  },
  cancelBtn: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  tvCancelBtn: {
    width: calcSizeForDevice(140),
  },
  webCancelBtn: {
    marginTop: Number(getResponsiveSize({ base: 24, md: 0 })),
    width: getResponsiveSize({ base: '100%', md: 130 }),
  },
  cancelBtnText: {
    fontSize: calcSizeForDevice(20),
    fontWeight: '600',
    color: '#888888',
  },
  deleteBtn: {
    height: calcSizeForDevice(54),
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: calcSizeForDevice(36, 24),
  },
  deleteBtnText: {
    fontSize: calcSizeForDevice(16, 20),
    fontWeight: '500',
    color: '#888888',
    marginLeft: calcSizeForDevice(12),
  },
  loadingContainer: {
    width: '100%',
    height: calcSizeForDevice(560),
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ProfileFormScreen;
